import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, comprimirImagem, prepararVideoOS, validarVideoOS, VIDEO_OS_MAX_SEGUNDOS } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PRIORIDADE } from '../lib/rotulos';
import Passos, { Passo } from '../components/Passos';
import SeletorFotos from '../components/SeletorFotos';
import GravadorVideo, { suportaGravador } from '../components/GravadorVideo';
import { usePodeIncluirDescricaoImagem } from '../lib/useConfigEmpresa';

interface Opcao { id: number; nome: string; }
interface Piso extends Opcao { unidade_id: number; }
interface Local extends Opcao { unidade_id: number; piso_id: number; }
interface Tecnico extends Opcao { unidades?: number[]; todas_unidades?: boolean; }
interface Ativo extends Opcao { unidade_id: number; piso_id: number; local_id: number; qr_code?: string | null; patrimonio?: string | null; cliente_ids?: number[]; }
interface Categoria extends Opcao { tipo: string; status?: string; }
interface Cliente extends Opcao { whatsapp?: string | null; }

interface FormState {
  unidade_id: string;
  piso_id: string;
  local_id: string;
  ativo_id: string;
  consumo_leitura_id: string;
  consumo_alerta_id: string;
  tecnico_id: string;
  cliente_id: string;
  tipo_os: string;
  prioridade: string;
  avaria: string;
  descricao: string;
  observacao: string;
  orcamento_valor: string;
  data_agendada: string;
  hora_agendada: string;
}

const fmtMoeda = (v: unknown): string => {
  const n = Number(v);
  const seguro = Number.isFinite(n) ? n : 0;
  return seguro.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const valorMoeda = (centavos: string): string => centavos ? `R$ ${fmtMoeda(Number(centavos) / 100)}` : '';

export default function OSNova() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { isPremium, isOrcamentoOsDisponivel, isSemUnidadeLocalPiso, user } = useAuth();
  const podeVerValoresMonetarios = !user?.somente_gestor_supervisor_pode_ver_valores_habilitado
    || ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeUsarOrcamentoOs = isOrcamentoOsDisponivel && podeVerValoresMonetarios;
  const [unidades, setUnidades] = useState<Opcao[]>([]);
  const [pisos, setPisos] = useState<Piso[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [tiposChamado, setTiposChamado] = useState<Categoria[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [descricoesImagens, setDescricoesImagens] = useState<string[]>([]);
  const [videoArquivo, setVideoArquivo] = useState<File | null>(null);
  const [descricaoVideo, setDescricaoVideo] = useState('');
  const podeDescricaoImagem = usePodeIncluirDescricaoImagem();
  const [validandoVideo, setValidandoVideo] = useState(false);
  const [gravadorAberto, setGravadorAberto] = useState(false);
  const videoGaleriaRef = useRef<HTMLInputElement>(null);
  const videoCameraRef = useRef<HTMLInputElement>(null);
  const [novoCli, setNovoCli] = useState({ nome: '', whatsapp: '' });
  const [mostrarNovoCli, setMostrarNovoCli] = useState(false);
  const [salvandoCli, setSalvandoCli] = useState(false);
  const [form, setForm] = useState<FormState>({
    unidade_id: params.get('unidade') || '',
    piso_id: params.get('piso') || '',
    local_id: params.get('local') || '',
    ativo_id: params.get('ativo') || '',
    consumo_leitura_id: params.get('consumo_leitura_id') || '',
    consumo_alerta_id: params.get('consumo_alerta_id') || '',
    tecnico_id: user?.pode_trazer_tecnico_associado_unidade ? '' : (user?.id ? String(user.id) : ''),
    cliente_id: '',
    tipo_os: params.get('tipo_os') || '',
    prioridade: 'media',
    avaria: '',
    descricao: params.get('descricao') || '',
    observacao: '',
    orcamento_valor: '',
    data_agendada: '',
    hora_agendada: '',
  });
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    Promise.all([
      api<Opcao[]>('/cadastros/unidades.php?somente_ativos=1'),
      api<Tecnico[]>('/cadastros/tecnicos.php'),
      api<Categoria[]>('/cadastros/categorias.php?somente_ativos=1'),
      api<Cliente[]>('/cadastros/clientes.php?somente_ativos=1'),
    ]).then(([u, t, c, cli]) => {
      setUnidades(u);
      setTecnicos(t);
      setTiposChamado(c);
      setClientes(cli);
      setForm((atual) => ({ ...atual, tipo_os: atual.tipo_os || c[0]?.nome || '' }));
    }).catch((e) => setErro(e.message));
  }, []);

  async function cadastrarClienteRapido() {
    if (!novoCli.nome.trim()) { setErro('Informe o nome do cliente.'); return; }
    setSalvandoCli(true);
    setErro('');
    try {
      const r = await api<{ id: number }>('/cadastros/clientes.php', {
        method: 'POST',
        body: { nome: novoCli.nome.trim(), whatsapp: novoCli.whatsapp || null },
      });
      const c: Cliente = { id: r.id, nome: novoCli.nome.trim(), whatsapp: novoCli.whatsapp || null };
      setClientes((atual) => [...atual, c]);
      set('cliente_id', String(c.id));
      setNovoCli({ nome: '', whatsapp: '' });
      setMostrarNovoCli(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvandoCli(false);
    }
  }

  useEffect(() => {
    if (isPremium) api<Ativo[]>('/ativos/ativos.php').then(setAtivos).catch(() => {});
  }, [isPremium]);

  useEffect(() => {
    if (!user?.id || form.tecnico_id || user?.pode_trazer_tecnico_associado_unidade) return;
    setForm((atual) => atual.tecnico_id ? atual : { ...atual, tecnico_id: String(user.id) });
  }, [user?.id, user?.pode_trazer_tecnico_associado_unidade, form.tecnico_id]);

  const montadoUni = useRef(false);
  const montadoPiso = useRef(false);

  useEffect(() => {
    if (form.unidade_id) api<Piso[]>('/cadastros/pisos.php?somente_ativos=1&unidade_id=' + form.unidade_id).then(setPisos).catch(() => {});
    else setPisos([]);
    if (montadoUni.current) {
      set('piso_id', '');
      set('local_id', '');
      setLocais([]);
    }
    montadoUni.current = true;
  }, [form.unidade_id]);

  useEffect(() => {
    if (form.piso_id) api<Local[]>('/cadastros/locais.php?somente_ativos=1&piso_id=' + form.piso_id).then(setLocais).catch(() => {});
    else setLocais([]);
    if (montadoPiso.current) set('local_id', '');
    montadoPiso.current = true;
  }, [form.piso_id]);

  const ativoSelecionado = ativos.find((a) => String(a.id) === form.ativo_id);
  const unidadeResponsavelId = form.unidade_id || (ativoSelecionado ? String(ativoSelecionado.unidade_id) : '');
  // Permissão "VER EQUIPAMENTOS NAS ORDENS DE SERVIÇO": ao escolher o cliente, a OS
  // passa a listar TODOS os equipamentos dele, sem depender do local selecionado.
  const podeVerEquipamentosDoCliente = Boolean(user?.pode_ver_equipamentos_nas_os);
  const filtrandoPorCliente = podeVerEquipamentosDoCliente && Boolean(form.cliente_id);
  const ativosDoCliente = useMemo(
    () => (filtrandoPorCliente ? ativos.filter((a) => (a.cliente_ids || []).includes(Number(form.cliente_id))) : []),
    [filtrandoPorCliente, ativos, form.cliente_id],
  );
  const ativosDoLocal = ativos.filter((a) => !form.local_id || a.local_id === Number(form.local_id));
  const ativosDisponiveis = filtrandoPorCliente ? ativosDoCliente : ativosDoLocal;
  const tecnicosDaUnidade = tecnicos.filter((t) => {
    if (!user?.pode_trazer_tecnico_associado_unidade) {
      return !form.unidade_id || !t.unidades?.length || t.unidades.includes(Number(form.unidade_id));
    }
    if (!unidadeResponsavelId) return true;
    return Boolean(t.todas_unidades) || Boolean(t.unidades?.includes(Number(unidadeResponsavelId)));
  });
  const podeAlterarTecnico = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const tecnicosResponsavel = !user?.pode_trazer_tecnico_associado_unidade && user?.id && !tecnicosDaUnidade.some((t) => t.id === user.id)
    ? [{ id: user.id, nome: user.nome || 'Usuário logado' }, ...tecnicosDaUnidade]
    : tecnicosDaUnidade;
  const opcoesTipoChamado = tiposChamado;
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Trocar o cliente (aqui ou no passo Responsável) não pode deixar para trás um
  // equipamento que não é dele.
  useEffect(() => {
    if (!filtrandoPorCliente || !form.ativo_id) return;
    if (ativosDoCliente.some((a) => String(a.id) === form.ativo_id)) return;
    set('ativo_id', '');
  }, [filtrandoPorCliente, form.ativo_id, ativosDoCliente]); // eslint-disable-line react-hooks/exhaustive-deps

  async function selecionarVideo(file?: File | null) {
    setErro('');
    if (!file) {
      setVideoArquivo(null);
      return;
    }
    setValidandoVideo(true);
    try {
      await validarVideoOS(file);
      setVideoArquivo(file);
    } catch (e) {
      setVideoArquivo(null);
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setValidandoVideo(false);
    }
  }

  function aoMudarArquivos(novos: File[]) {
    setDescricoesImagens((atual) => novos.map((f) => {
      const idx = arquivos.indexOf(f);
      return idx >= 0 ? (atual[idx] || '') : '';
    }));
    setArquivos(novos);
  }

  async function uploadImagens(osId: number) {
    const lista = arquivos.slice(0, 5);
    for (let i = 0; i < lista.length; i++) {
      const fd = new FormData();
      fd.append('ordem_servico_id', String(osId));
      fd.append('tipo', 'abertura');
      if (podeDescricaoImagem && descricoesImagens[i]) fd.append('descricao', descricoesImagens[i]);
      fd.append('imagem', await comprimirImagem(lista[i]));
      await api('/os/os_imagens.php', { method: 'POST', body: fd, isForm: true });
    }
    if (videoArquivo) {
      const fd = new FormData();
      fd.append('ordem_servico_id', String(osId));
      fd.append('tipo', 'abertura');
      if (podeDescricaoImagem && descricaoVideo) fd.append('descricao', descricaoVideo);
      fd.append('imagem', await prepararVideoOS(videoArquivo));
      await api('/os/os_imagens.php', { method: 'POST', body: fd, isForm: true });
    }
  }

  async function enviar() {
    setErro('');
    setSalvando(true);
    const temAnexos = Boolean(arquivos.length || videoArquivo);
    try {
      const body = {
        ...(form.unidade_id ? { unidade_id: Number(form.unidade_id) } : {}),
        ...(form.piso_id ? { piso_id: Number(form.piso_id) } : {}),
        ...(form.local_id ? { local_id: Number(form.local_id) } : {}),
        ...(form.ativo_id ? { ativo_id: Number(form.ativo_id) } : {}),
        ...(form.consumo_leitura_id ? { consumo_leitura_id: Number(form.consumo_leitura_id) } : {}),
        ...(form.consumo_alerta_id ? { consumo_alerta_id: Number(form.consumo_alerta_id) } : {}),
        ...(form.tecnico_id ? { tecnico_id: Number(form.tecnico_id) } : {}),
        ...(form.cliente_id ? { cliente_id: Number(form.cliente_id) } : {}),
        tipo_os: form.tipo_os,
        prioridade: form.prioridade,
        avaria: form.avaria || null,
        descricao: form.descricao || null,
        observacao: form.observacao || null,
        ...(podeUsarOrcamentoOs ? { orcamento_valor: valorMoeda(form.orcamento_valor) || null } : {}),
        data_agendada: form.data_agendada || null,
        hora_agendada: form.hora_agendada || null,
        // O anexo sobe DEPOIS, em outra requisicao. Sem este aviso o push do
        // tecnico sairia antes da imagem existir, e chegaria sem foto.
        ...(temAnexos ? { aguardar_anexos: true } : {}),
      };
      const data = await api<{ id: number }>('/os/os_criar.php', { method: 'POST', body });
      if (temAnexos) {
        try {
          await uploadImagens(data.id);
        } finally {
          // No finally: se um anexo falhar, o tecnico ainda precisa ser avisado
          // da OS. O endpoint e idempotente, entao chamar aqui nao duplica.
          await api('/os/os_notificar_tecnico.php', {
            method: 'POST',
            body: { ordem_servico_id: data.id },
          }).catch(() => {});
        }
      }
      nav('/app/os/' + data.id);
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
      setSalvando(false);
    }
  }

  const tituloSecao = {
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase' as const,
    letterSpacing: '.08em',
    color: 'var(--texto-suave)',
    margin: '18px 2px 8px',
  };
  const nomeDe = (arr: Opcao[], id: string) => arr.find((x) => String(x.id) === id)?.nome || (user?.id && String(user.id) === id ? user.nome : '—');

  const passos: Passo[] = [
    {
      titulo: 'Tipo',
      node: (
        <div className="card">
          <div className="grid cols2">
            <div>
              <label>Tipo de chamado *</label>
              <select value={form.tipo_os} onChange={(e) => set('tipo_os', e.target.value)} required disabled={!opcoesTipoChamado.length}>
                <option value="">Selecione o tipo de chamado</option>
                {opcoesTipoChamado.map((c) => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
              {!opcoesTipoChamado.length && (
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  Nenhum tipo de chamado cadastrado ainda. Cadastre um em Categorias antes de abrir uma OS.
                </p>
              )}
            </div>
            <div>
              <label>Prioridade</label>
              <select value={form.prioridade} onChange={(e) => set('prioridade', e.target.value)}>
                {Object.entries(PRIORIDADE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>
      ),
    },
    {
      titulo: 'Localização',
      node: (
        <div className="card">
          <div className="grid cols2">
            <div>
              <label>Unidade</label>
              <select value={form.unidade_id} onChange={(e) => set('unidade_id', e.target.value)}>
                <option value="">Selecione a unidade</option>
                {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div>
              <label>Piso</label>
              <select value={form.piso_id} onChange={(e) => set('piso_id', e.target.value)} disabled={!form.unidade_id}>
                <option value="">{form.unidade_id ? 'Selecione o piso' : 'Escolha a unidade primeiro'}</option>
                {pisos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Local</label>
              <select value={form.local_id} onChange={(e) => set('local_id', e.target.value)} disabled={!form.piso_id}>
                <option value="">{form.piso_id ? 'Selecione o local' : 'Escolha o piso primeiro'}</option>
                {locais.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </select>
            </div>
          </div>
        </div>
      ),
    },
    ...(isPremium ? [{
      titulo: user?.rotulo_nome_ativo || 'Equipamento',
      node: (
        <div className="card">
          {podeVerEquipamentosDoCliente && (
            <>
              <label>Cliente do chamado</label>
              <select value={form.cliente_id} onChange={(e) => { set('cliente_id', e.target.value); set('ativo_id', ''); }}>
                <option value="">Sem cliente (equipamentos do local)</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                Ao escolher o cliente, a lista abaixo mostra todos os equipamentos dele — mesmo os de outro local.
              </p>
            </>
          )}
          <label style={podeVerEquipamentosDoCliente ? { marginTop: 16 } : undefined}>Equipamento (opcional)</label>
          <select value={form.ativo_id} onChange={(e) => set('ativo_id', e.target.value)}>
            <option value="">Sem equipamento (OS por local)</option>
            {ativosDisponiveis.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}{a.patrimonio ? ' · ' + a.patrimonio : ''} {a.qr_code ? '- ' + a.qr_code : ''}
              </option>
            ))}
          </select>
          {filtrandoPorCliente && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
              {ativosDoCliente.length
                ? `${ativosDoCliente.length} equipamento(s) vinculado(s) a este cliente.`
                : 'Este cliente ainda não tem equipamentos vinculados. Vincule em Equipamentos.'}
            </p>
          )}
        </div>
      ),
    } as Passo] : []),
    {
      titulo: 'Responsável',
      node: (
        <div className="card">
          <label>Técnico</label>
          <select value={form.tecnico_id} onChange={(e) => set('tecnico_id', e.target.value)} disabled={!podeAlterarTecnico}>
            <option value="">Responsável abre a OS</option>
            {tecnicosResponsavel.map((t) => <option key={t.id} value={t.id}>{t.id === user?.id ? t.nome + ' (você)' : t.nome}</option>)}
          </select>
          {!podeAlterarTecnico && <p style={{ color: 'var(--texto-suave)', margin: '6px 0 0', fontSize: 13 }}>A OS será atribuída ao usuário logado.</p>}
          <div style={tituloSecao}>Cliente</div>
          {!mostrarNovoCli ? (
            <>
              <label>Cliente (para envio da OS por WhatsApp)</label>
              <select value={form.cliente_id} onChange={(e) => set('cliente_id', e.target.value)}>
                <option value="">Sem cliente</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.whatsapp ? ' · ' + c.whatsapp : ''}</option>)}
              </select>
              <button type="button" className="btn sec sm" style={{ marginTop: 8 }} onClick={() => setMostrarNovoCli(true)}>+ Novo cliente</button>
            </>
          ) : (
            <div style={{ border: '1px dashed var(--borda)', borderRadius: 10, padding: 12 }}>
              <label>Nome do cliente *</label>
              <input className="input" value={novoCli.nome} onChange={(e) => setNovoCli({ ...novoCli, nome: e.target.value })} />
              <label>WhatsApp</label>
              <input className="input" value={novoCli.whatsapp} onChange={(e) => setNovoCli({ ...novoCli, whatsapp: e.target.value })} placeholder="(11) 99999-9999" />
              <div className="form-actions">
                <button type="button" className="btn sec" onClick={() => { setMostrarNovoCli(false); setNovoCli({ nome: '', whatsapp: '' }); }}>Cancelar</button>
                <button type="button" className="btn" disabled={salvandoCli} onClick={cadastrarClienteRapido}>{salvandoCli ? 'Salvando...' : 'Salvar cliente'}</button>
              </div>
            </div>
          )}
          <div style={tituloSecao}>Agendamento</div>
          <div className="grid cols2">
            <div>
              <label>Data agendada</label>
              <input className="input" type="date" value={form.data_agendada} onChange={(e) => set('data_agendada', e.target.value)} />
            </div>
            <div>
              <label>Hora agendada</label>
              <input className="input" type="time" value={form.hora_agendada} onChange={(e) => set('hora_agendada', e.target.value)} />
            </div>
          </div>
        </div>
      ),
    },
    ...(podeUsarOrcamentoOs ? [{
      titulo: 'Orçamento',
      node: (
        <div className="card">
          <label>Valor do orçamento</label>
          <input
            className="input"
            inputMode="numeric"
            value={valorMoeda(form.orcamento_valor)}
            onChange={(e) => set('orcamento_valor', e.target.value.replace(/\D/g, ''))}
            placeholder="R$ 0,00"
          />
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
            Campo opcional. O valor será registrado em moeda brasileira.
          </p>
        </div>
      ),
    } as Passo] : []),
    {
      titulo: 'Descrição',
      node: (
        <div className="card">
          <label>Avaria</label>
          <input className="input" value={form.avaria} onChange={(e) => set('avaria', e.target.value)} placeholder="Resumo curto da avaria" />
          <label>Descrição</label>
          <textarea rows={4} value={form.descricao} onChange={(e) => set('descricao', e.target.value)} placeholder="Descreva o problema com detalhes" />
          <label>Observação</label>
          <textarea rows={3} value={form.observacao} onChange={(e) => set('observacao', e.target.value)} placeholder="Informações adicionais (opcional)" />
        </div>
      ),
    },
    {
      titulo: 'Evidências',
      node: (
        <div className="card">
          <label>Imagens da abertura</label>
          <SeletorFotos arquivos={arquivos} onChange={aoMudarArquivos} max={5} disabled={salvando} />
          {podeDescricaoImagem && arquivos.length > 0 && (
            <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              {arquivos.map((arq, i) => (
                <input
                  key={arq.name + i}
                  className="input"
                  placeholder={`Descrição da imagem ${i + 1} (opcional)`}
                  value={descricoesImagens[i] || ''}
                  disabled={salvando}
                  onChange={(e) => setDescricoesImagens((atual) => {
                    const novo = [...atual];
                    novo[i] = e.target.value;
                    return novo;
                  })}
                />
              ))}
            </div>
          )}
          {isPremium ? (
            <div style={{ marginTop: 16 }}>
              <label>Video da abertura</label>
              <input
                ref={videoGaleriaRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                disabled={salvando}
                onChange={(ev) => {
                  selecionarVideo(ev.currentTarget.files?.[0] || null);
                  ev.currentTarget.value = '';
                }}
              />
              <input
                ref={videoCameraRef}
                type="file"
                accept="video/*"
                capture="environment"
                style={{ display: 'none' }}
                disabled={salvando}
                onChange={(ev) => {
                  selecionarVideo(ev.currentTarget.files?.[0] || null);
                  ev.currentTarget.value = '';
                }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn sec sm" disabled={salvando || validandoVideo || !!videoArquivo} onClick={() => videoGaleriaRef.current?.click()}>
                  Selecionar vídeo
                </button>
                <button
                  type="button"
                  className="btn sec sm"
                  disabled={salvando || validandoVideo || !!videoArquivo}
                  onClick={() => { if (suportaGravador()) setGravadorAberto(true); else videoCameraRef.current?.click(); }}
                >
                  Gravar vídeo
                </button>
                {validandoVideo && <span className="badge">Validando vídeo...</span>}
              </div>
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                Máximo de {VIDEO_OS_MAX_SEGUNDOS} segundos e 8 MB. Limite de 2 vídeos por OS ao todo.
              </p>
              {videoArquivo && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="badge">{videoArquivo.name}</span>
                  <button type="button" className="btn sec sm" disabled={salvando} onClick={() => { setVideoArquivo(null); setDescricaoVideo(''); }}>Remover vídeo</button>
                </div>
              )}
              {podeDescricaoImagem && videoArquivo && (
                <input
                  className="input"
                  style={{ marginTop: 8 }}
                  placeholder="Descrição do vídeo (opcional)"
                  value={descricaoVideo}
                  disabled={salvando}
                  onChange={(e) => setDescricaoVideo(e.target.value)}
                />
              )}
              {gravadorAberto && (
                <GravadorVideo
                  maxSegundos={VIDEO_OS_MAX_SEGUNDOS}
                  onFechar={() => setGravadorAberto(false)}
                  onGravado={(file) => { setVideoArquivo(file); setErro(''); setGravadorAberto(false); }}
                />
              )}
            </div>
          ) : (
            <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>
              Anexar video da abertura faz parte do plano Premium.
            </p>
          )}
        </div>
      ),
    },
    {
      titulo: 'Revisão',
      node: (
        <div className="card">
          <h3>Revisão</h3>
          <div className="os-dados">
            <div className="os-campo"><span className="k">Tipo / Prioridade</span><span className="v">{(form.tipo_os || '—') + ' · ' + (PRIORIDADE[form.prioridade] || form.prioridade)}</span></div>
            <div className="os-campo full"><span className="k">Local</span><span className="v">{[nomeDe(unidades, form.unidade_id), nomeDe(pisos, form.piso_id), nomeDe(locais, form.local_id)].filter((x) => x !== '—').join(' / ') || '—'}</span></div>
            {isPremium && <div className="os-campo"><span className="k">{user?.rotulo_nome_ativo || 'Equipamento'}</span><span className="v">{nomeDe(ativos, form.ativo_id)}</span></div>}
            {!!form.consumo_leitura_id && <div className="os-campo"><span className="k">Leitura de consumo</span><span className="v">#{form.consumo_leitura_id}</span></div>}
            <div className="os-campo"><span className="k">Técnico</span><span className="v">{nomeDe(tecnicos, form.tecnico_id)}</span></div>
            <div className="os-campo"><span className="k">Cliente</span><span className="v">{form.cliente_id ? nomeDe(clientes, form.cliente_id) : '—'}</span></div>
            {podeUsarOrcamentoOs && <div className="os-campo"><span className="k">Orçamento</span><span className="v">{form.orcamento_valor ? valorMoeda(form.orcamento_valor) : '—'}</span></div>}
            <div className="os-campo"><span className="k">Agendamento</span><span className="v">{form.data_agendada ? form.data_agendada.split('-').reverse().join('/') + (form.hora_agendada ? ' ' + form.hora_agendada : '') : 'Sem agendamento'}</span></div>
            {form.avaria && <div className="os-campo full"><span className="k">Avaria</span><span className="v bloco">{form.avaria}</span></div>}
            {form.descricao && <div className="os-campo full"><span className="k">Descrição</span><span className="v bloco">{form.descricao}</span></div>}
            <div className="os-campo"><span className="k">Imagens</span><span className="v">{arquivos.length}</span></div>
            {isPremium && <div className="os-campo"><span className="k">Video</span><span className="v">{videoArquivo ? videoArquivo.name : 'Nenhum'}</span></div>}
          </div>
          <div className="form-actions">
            <button type="button" className="btn sec" onClick={() => nav('/app/os')}>Cancelar</button>
            <button type="button" className="btn" disabled={salvando || !form.tipo_os} onClick={enviar}>{salvando ? 'Criando...' : 'Criar OS'}</button>
          </div>
        </div>
      ),
    },
  ].filter((passo) => !isSemUnidadeLocalPiso || passo.titulo !== 'Localização');

  return (
    <div>
      <div className="titulo-pg"><h1>Nova Ordem de Serviço</h1></div>
      {erro && <div className="msg-erro">{erro}</div>}
      <Passos passos={passos} rotuloFim="Revise e crie a OS" />
    </div>
  );
}
